import configs from "./configs";
import { getAuthHeaders } from "./getToken";
import { getActiveWorkspaceId } from "./workspace-scope-utils";

export function buildWorkspaceMediaEndpoint(persistenceApiUrl, workspaceId) {
  if (!persistenceApiUrl || !workspaceId) return null;
  return `${persistenceApiUrl.replace(/\/+$/, "")}/workspaces/${encodeURIComponent(workspaceId)}/media`;
}

export function buildWorkspaceMediaSpawnPayload(acquisition, fallbackSrc = null) {
  const asset = acquisition?.asset || null;
  const placement = acquisition?.placement || {};
  const mediaOptions = placement.mediaOptions || {};
  const assetSrc =
    asset?.apiUrl || asset?.api_url || (asset?.uuid ? `${configs.PERSISTENCE_API_URL}/assets/${asset.uuid}` : null);

  return {
    src: placement.src || asset?.url || fallbackSrc,
    mediaOptions,
    fileIsOwned: true,
    assetSrc,
    asset
  };
}

export function getWorkspaceMediaAcquisitionErrorMessage(error) {
  const body = error?.body || {};
  if (typeof body.message === "string" && body.message.trim()) {
    return body.message;
  }

  if (error?.status === 429 || error?.response?.status === 429) {
    return "The site is rate limiting this request. Try again in a moment.";
  }

  if (error?.status === 403 || error?.response?.status === 403) {
    return "This URL cannot be accessed. It may be private or blocked by the source site.";
  }

  if (error?.status === 404 || error?.response?.status === 404) {
    return "The URL could not be downloaded. It may be unavailable or no longer exist.";
  }

  if (typeof body.error === "string" && body.error.trim() && body.error !== "media_download_failed") {
    return body.error;
  }

  return "We couldn’t add that link. Check the URL and try again.";
}

export async function acquireWorkspaceMediaForSpawn(
  url,
  {
    quality = "high",
    version = 1,
    workspaceId = getActiveWorkspaceId(),
    persistenceApiUrl = configs.PERSISTENCE_API_URL,
    fetchImpl = fetch,
    getAuthHeadersImpl = getAuthHeaders
  } = {}
) {
  const endpoint = buildWorkspaceMediaEndpoint(persistenceApiUrl, workspaceId);
  if (!endpoint) return null;

  const authHeaders = await getAuthHeadersImpl(endpoint, "POST");
  if (!authHeaders.Authorization) return null;

  const response = await fetchImpl(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      ...authHeaders
    },
    body: JSON.stringify({
      media: {
        url,
        quality
      },
      version
    })
  });

  if (!response.ok) {
    const error = new Error(`Failed to acquire media URL: ${response.status}`);
    error.status = response.status;
    error.response = response;
    try {
      error.body = await response.json();
    } catch (_) {
      // Ignore non-JSON error bodies.
    }
    throw error;
  }

  const acquisition = await response.json();
  return buildWorkspaceMediaSpawnPayload(acquisition, url);
}
